/**
 * tm/components/ConsultCompanies.tsx — 상담 결과 「상담한 회사」 (2026-10-18 지도사 「한 번에 회사 두 개 상담하는 경우도 있어 · 관계사 같은 거 —
 *  상담 결과 등록할 때 회사를 각각 등록 · 상담 결과 입력은 하나로 · 진행할 업무 등록할 때 어느 회사로 하는 건지 선택」)
 *
 *  주 회사(상담에 걸린 회사)는 고정 · 함께 상담한 회사를 찾아 넣거나(업체 이름 검색) 없으면 새 회사로 등록(소개 경로 · 담당 FP · WM 은 주 회사와 같게)
 *  넣거나 빼면 바로 저장(consultations.extra_company_ids) — 업무 등록 화면이 이 목록으로 「회사」를 고른다
 *  빼려는 회사에 이 상담 업무가 있으면 빼지 않고 알려 준다(업무가 주인 없는 회사로 남지 않게)
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Company, Consultation, Task } from '../types';
import * as api from '../api';
import { C, T, R } from './ui';

export interface CoRef { id: string; name: string }

/** 상담의 함께 상담한 회사 id 목록 (jsonb 가 글자로 올 때도) */
export function extraIdsOf(c: Pick<Consultation, 'extra_company_ids'>): string[] {
  let x: unknown = c.extra_company_ids;
  if (typeof x === 'string') { try { x = JSON.parse(x); } catch { x = []; } }
  return Array.isArray(x) ? x.map(String).filter(Boolean) : [];
}

export default function ConsultCompanies({ consultation, mainCompany, tasks, onChange, onToast }: {
  consultation: Consultation; mainCompany: Company | null; tasks: Task[];
  onChange: (extra: CoRef[]) => void; onToast: (msg: string) => void;
}) {
  const [extra, setExtra] = useState<CoRef[]>([]);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<CoRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState('');
  const seq = useRef(0);

  // 처음 — 저장돼 있던 관계사 이름 불러오기
  useEffect(() => {
    const ids = extraIdsOf(consultation);
    if (!ids.length) { setExtra([]); onChange([]); return; }
    Promise.all(ids.map(id => api.getCompany(id).then(r => (r.data ? { id, name: r.data.name } : null)).catch(() => null)))
      .then(list => { const ok = list.filter((x): x is CoRef => !!x); setExtra(ok); onChange(ok); });
  }, [consultation.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 업체 이름 검색(입력 멈춘 뒤 0.25초)
  useEffect(() => {
    const t = q.trim();
    if (!adding || t.length < 1) { setHits([]); return undefined; }
    const my = ++seq.current;
    const h = setTimeout(() => {
      api.getCompanies({ search: t, limit: 8 }).then(r => {
        if (my !== seq.current) return;
        const rows = (r.data && Array.isArray(r.data.companies) ? r.data.companies : []) as Company[];
        setHits(rows.filter(c => c.id !== consultation.company_id && !extra.some(x => x.id === c.id)).map(c => ({ id: c.id, name: c.name })));
      }).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(h);
  }, [q, adding, consultation.company_id, extra]);

  async function save(next: CoRef[]): Promise<boolean> {
    try {
      await api.updateConsultation(consultation.id, { extra_company_ids: next.map(x => x.id) });
      setExtra(next); onChange(next);
      return true;
    } catch (e) { onToast(e instanceof Error ? e.message : '저장하지 못했어요'); return false; }
  }
  async function pick(co: CoRef) {
    setBusy(true);
    if (await save([...extra, co])) { setAdding(false); setQ(''); setHits([]); onToast(`${co.name}을(를) 함께 상담한 회사로 넣었어요`); }
    setBusy(false);
  }
  /** 새 회사 — 주 회사의 소개 경로 · 담당 FP · WM 을 그대로 */
  async function createNew() {
    const name = q.trim();
    if (!name) return;
    setBusy(true);
    try {
      const m = mainCompany;
      const fpIds = consultation.fp_id ? [consultation.fp_id] : ((m && Array.isArray(m.fps)) ? m.fps.map(f => f.fp_id).filter(Boolean) : []);
      const r = await api.createCompanyOrLink({
        name, fp_ids: fpIds, tm_status: '대기', source: '관계사(상담)',
        ...(m && m.referral_type ? { referral_type: m.referral_type, referral_detail: m.referral_detail } : {}),
        ...(m && m.wm_id ? { wm_id: m.wm_id } : {}),
      } as Partial<Company> & { fp_ids?: string[] });
      const co = { id: r.id, name: r.existed && r.existingName ? r.existingName : name };
      if (r.existed) onToast(`같은 이름의 업체가 있어 그 업체로 넣었어요 (${co.name})`);
      await pick(co);
    } catch (e) { onToast(e instanceof Error ? e.message : '등록하지 못했어요'); }
    setBusy(false);
  }
  async function remove(co: CoRef) {
    const n = tasks.filter(t => t.company_id === co.id && t.consult_id === consultation.id).length;
    if (n) { setWarn(`${co.name}에 이 상담 업무 ${n}건이 있어요. 아래 「진행할 업무」에서 업무를 지우거나 다른 회사로 옮긴 뒤 빼 주세요`); return; }
    setWarn('');
    await save(extra.filter(x => x.id !== co.id));
  }

  const chip = (main: boolean): CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 260, height: 30, padding: '0 10px', borderRadius: 15,
    border: `1px solid ${main ? C.accent : C.border}`, background: main ? '#EBF3FB' : C.white, color: main ? C.accent : C.text, fontSize: T.xs, fontWeight: 700,
  });
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={chip(true)} title={consultation.company_name || ''}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{consultation.company_name || '주 회사'}</span>
          <span style={{ fontWeight: 500, color: C.sub, flexShrink: 0 }}>주 회사</span>
        </span>
        {extra.map(co => (
          <span key={co.id} style={chip(false)} title={co.name}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{co.name}</span>
            <button type="button" aria-label={`${co.name} 빼기`} onClick={() => void remove(co)}
              style={{ border: 'none', background: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
          </span>
        ))}
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setWarn(''); }}
            style={{ height: 30, padding: '0 12px', borderRadius: 15, border: `1px dashed ${C.accent}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>+ 함께 상담한 회사</button>
        )}
      </div>
      {adding && (
        <div style={{ marginTop: 8, padding: 10, borderRadius: R.md, border: `1px solid ${C.border}`, background: C.bgAlt }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="업체 이름으로 찾기 (관계사 등)"
              style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none' }} />
            <button type="button" onClick={() => { setAdding(false); setQ(''); setHits([]); }}
              style={{ padding: '0 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>닫기</button>
          </div>
          {q.trim() && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {hits.map(h => (
                <button key={h.id} type="button" disabled={busy} onClick={() => void pick(h)}
                  style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 6, border: 'none', background: C.white, color: C.text, fontSize: T.xs, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</button>
              ))}
              <button type="button" disabled={busy} onClick={() => void createNew()}
                style={{ textAlign: 'left', padding: '7px 10px', borderRadius: 6, border: `1px dashed ${C.border}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 700, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                「{q.trim()}」 새 회사로 등록 <span style={{ fontWeight: 500, color: C.muted }}>소개 경로 · 담당 FP 는 {consultation.company_name || '주 회사'}와 같게</span>
              </button>
            </div>
          )}
        </div>
      )}
      {warn && <div style={{ marginTop: 6, fontSize: T.xs, color: '#92400E' }}>{warn}</div>}
      <div style={{ marginTop: 6, fontSize: T.xs, color: C.muted, lineHeight: 1.6 }}>관계사처럼 한 번에 여러 회사를 상담했으면 함께 넣어 주세요. 상담 결과는 하나로 저장되고, 진행할 업무는 회사를 골라 등록해요.</div>
    </div>
  );
}
